import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  Column,
  FormController,
  MuiBox,
  MuiDateFieldFormController,
  MuiLink,
  MuiNotesToolTip,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  RowValue,
} from "../../../../../../components";
import { AmountWithSign } from "../../../../../../components/amountWithSign/AmountWithSign";
import {
  STATUS_BADGE_TYPE,
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../components/statusBadge/StatusBadge";
import {
  DELIVERABLE_FRAMEWORK_TYPE,
  INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY,
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUserFormat,
  getAmountWithCurrency,
  getFullName,
  getValueOrBlank,
  isStatusClosed,
  isStatusInvoiced,
  isValueIncludedIgnoreCase,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { currencyListSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { projectStatusSelector } from "../../../../../../redux/slices/project/projectSelector";
import { Currency } from "../../../../../createClient/modelsAndSchemas/Models";
import { useDownloadInvoiceToExcel } from "../../../../../createProgram/hooks/useDownloadInvoiceToExcel";
import {
  ProjectInvoice,
  ProjectInvoiceHistoryColumnKey,
  ProjectInvoiceItem,
} from "../../../../modelsAndSchemas/Models";
import { projectInvoiceHistorySiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

const pageId = `${PAGE_IDS.PROGRAM_TEMPLATE}-invoice-history-list`;

interface ProjectInvoiceHistoryListProps {
  onReverseClick: (selectedInvoice: ProjectInvoice) => void;
  onViewInvoiceClick: (selectedInvoice: ProjectInvoice) => void;
  onExportInvoiceClick: (selectedInvoice: ProjectInvoice) => void;
  onManageRevenueSplitClick: (selectedInvoice: ProjectInvoiceItem) => void;
  onViewRevenueSplitMigratedClick: (
    selectedInvoice: ProjectInvoiceItem,
  ) => void;
  isViewOnly?: boolean;
  data: ProjectInvoiceItem[] | undefined; // Accept invoice data from parent, type as needed
  currency?: Currency;
}
export const ProjectInvoiceHistoryList = ({
  onReverseClick,
  onViewInvoiceClick,
  onExportInvoiceClick,
  onManageRevenueSplitClick,
  onViewRevenueSplitMigratedClick,
  isViewOnly,
  data,
  currency,
}: ProjectInvoiceHistoryListProps) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const invoiceHistoryTableRef = useRef<{ resetPagination: () => void }>(null);
  const getHistoryLabel = (
    key: keyof typeof projectInvoiceHistorySiteCoreKeys,
  ) => {
    return fetchLabel(
      projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
      projectInvoiceHistorySiteCoreKeys[key],
    );
  };
  const { control } = useForm<Partial<ProjectInvoice>>();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );
  const currencyList = useSelector(currencyListSelector);
  const [state, setState] = useState<{
    filteredListInvoiceHistory: ProjectInvoiceItem[];
    originalListInvoiceHistory: ProjectInvoiceItem[];
  }>({
    filteredListInvoiceHistory: [],
    originalListInvoiceHistory: [],
  });
  const projectStatusId = useSelector(projectStatusSelector);
  const isProjectStatusNotClosed = useMemo(
    () => !isStatusClosed(projectStatusId as StatusBadgeType),
    [projectStatusId],
  );
  useEffect(() => {
    if (data?.length) {
      setState((prevState) => ({
        ...prevState,
        filteredListInvoiceHistory: data,
        originalListInvoiceHistory: data,
      }));
      invoiceHistoryTableRef.current?.resetPagination();
    }
  }, [data]);

  const handleOnViewClick = (selectedProjectInvoice: ProjectInvoice) => {
    onViewInvoiceClick(selectedProjectInvoice);
  };

  const handleManageRevenueSplitClick = (selectedInvoice: ProjectInvoice) => {
    onManageRevenueSplitClick(selectedInvoice as ProjectInvoiceItem);
  };

  const handleViewRevenueSplitMigratedClick = (
    selectedInvoice: ProjectInvoice,
  ) => {
    onViewRevenueSplitMigratedClick(selectedInvoice as ProjectInvoiceItem);
  };

  const handleOnReverseClick = (selectedProjectInvoice: ProjectInvoice) => {
    // reverse logic write here
    onReverseClick(selectedProjectInvoice);
  };
  const handleOnExportInvoiceClick = (selectedInvoice: ProjectInvoice) => {
    onExportInvoiceClick(selectedInvoice);
  };

  const { downloadInvoiceExcel } = useDownloadInvoiceToExcel();
  const handleOnDownloadInvoiceClick = (selectedInvoice: ProjectInvoice) => {
    downloadInvoiceExcel(
      selectedInvoice.id,
      DELIVERABLE_FRAMEWORK_TYPE.PROJECT,
    );
  };

  const filters = useWatch({ control });

  useEffect(() => {
    appliedFilters(state.originalListInvoiceHistory);
  }, [filters, state.originalListInvoiceHistory]);

  const appliedFilters = (invoiceData: ProjectInvoiceItem[] = []) => {
    if (!invoiceData.length) return;

    let filteredListInvoiceHistory = invoiceData.map((item) => ({
      ...item,
      reversalReason:
        item?.reversalReason !== INVOICE_REVERSAL_REASON_OTHERS_SITE_CORE_KEY
          ? fetchValueByKeyOfDropdown(
              projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
              projectInvoiceHistorySiteCoreKeys.confirmationPopUpInvoiceReversalReasonList,
              item?.reversalReason ?? "",
            )
          : getValueOrBlank(item?.reversalComments),
    }));

    const {
      number,
      date,
      statusId,
      totalInvoiceAmount,
      reversalReason,
      issuedBy,
      purchaseOrder,
    } = filters || {};

    filteredListInvoiceHistory = filteredListInvoiceHistory.filter((item) => {
      const issuedByMatches =
        isValueIncludedIgnoreCase(item.issuedBy?.firstName, issuedBy) ||
        isValueIncludedIgnoreCase(item.issuedBy?.lastName, issuedBy) ||
        isValueIncludedIgnoreCase(item.issuedBy?.email, issuedBy);
      return (
        isValueIncludedIgnoreCase(item.number, number) &&
        isValueIncludedIgnoreCase(
          convertToUserFormat(item.date, userCountryDateFormat),
          date,
        ) &&
        isValueIncludedIgnoreCase(item.statusId, statusId) &&
        isValueIncludedIgnoreCase(item.reversalReason, reversalReason) &&
        isValueIncludedIgnoreCase(
          item.totalInvoiceAmount,
          totalInvoiceAmount,
        ) &&
        issuedByMatches &&
        isValueIncludedIgnoreCase(item.purchaseOrder?.number, purchaseOrder)
      );
    });

    setState((prevState: any) => ({
      ...prevState,
      filteredListInvoiceHistory: filteredListInvoiceHistory,
    }));
    invoiceHistoryTableRef.current?.resetPagination();
  };

  const columns: Column[] = [
    {
      id: ProjectInvoiceHistoryColumnKey.projectNumber,
      label: getHistoryLabel("invoiceNumberTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) =>
        hasModulePermission(MODULE_PERMISSIONS.PROJECT_VIEW_INVOICE) ? (
          <MuiLink
            onClick={() => handleOnViewClick(row as unknown as ProjectInvoice)}
          >
            <MuiBox>
              <MuiTypography variant="h3" className="Link">
                {row[ProjectInvoiceHistoryColumnKey.projectNumber]}
              </MuiTypography>
            </MuiBox>
          </MuiLink>
        ) : (
          <MuiBox>
            <MuiTypography variant="h3" className="Link">
              {row[ProjectInvoiceHistoryColumnKey.projectNumber]}
            </MuiTypography>
          </MuiBox>
        ),
    },
    {
      id: ProjectInvoiceHistoryColumnKey.projectDate,
      label: getHistoryLabel("invoicedDateTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3" className="">
            {row?.date
              ? convertToUserFormat(row.date, userCountryDateFormat)
              : ""}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ProjectInvoiceHistoryColumnKey.invoiceStatusId,
      label: getHistoryLabel("invoiceStatusTableColumn"),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <StatusBadge
          status={row.statusId}
          label={fetchValueByKeyOfDropdown(
            projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
            projectInvoiceHistorySiteCoreKeys.invoiceStatusList,
            row.statusId,
          )}
        />
      ),
    },
    {
      id: ProjectInvoiceHistoryColumnKey.projectReversalReason,
      label: getHistoryLabel("reversalReasonTableColumn"),
      isSortVisible: true,
      className: "cell-program-status",
      render: (row: RowProps) => (
        <MuiNotesToolTip message={row?.reversalReason} />
      ),
    },
    {
      id: ProjectInvoiceHistoryColumnKey.projectTotalInvoiceAmount,
      label: getHistoryLabel("totalInvoiceAmountTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const amtWithCurrency = getAmountWithCurrency(
          row.totalInvoiceAmount ?? 0,
          currency?.name ?? "",
        );
        const sign = isStatusInvoiced(row.statusId) ? "+" : "-";
        return <AmountWithSign currency={amtWithCurrency} sign={sign} />;
      },
    },
    {
      id: ProjectInvoiceHistoryColumnKey.projectIssuedBy,
      label: getHistoryLabel("issuedByTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const issuedBy = row.issuedBy;
        return (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {getFullName(issuedBy?.firstName, issuedBy?.lastName, true)}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    {
      id: ProjectInvoiceHistoryColumnKey.projectPurchaseOrder,
      label: getHistoryLabel("poNumberTableColumn"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const purchaseOrder = row.purchaseOrder;
        return (
          <MuiBox>
            <MuiTypography variant="h3" className="">
              {purchaseOrder?.number}
            </MuiTypography>
          </MuiBox>
        );
      },
    },
    ...(hasModulePermission(MODULE_PERMISSIONS.PROGRAM_INVOICE_REVERSAL)
      ? [
          {
            id: "action",
            label: getHistoryLabel("actionTableColumn"),
            render: (row: RowProps) => {
              let actions = [];

              if (
                isProjectStatusNotClosed &&
                !isViewOnly &&
                !(
                  row?.isInvoiceReversed ||
                  row?.statusId === STATUS_BADGE_TYPE.reversed
                )
              ) {
                actions.push({
                  onClick: () => handleOnReverseClick(row),
                  dataTestId: "reverse-invoice",
                  label: getHistoryLabel("reverseInvoice"),
                });
              }

              if (row?.hasMigratedRevenueSplits) {
                actions.push({
                  onClick: () => handleViewRevenueSplitMigratedClick(row),
                  dataTestId: "project-view-revenue-split-migrated",
                  label: getHistoryLabel("viewRevenueSplitMigrated"),
                });
              }
              if (row?.hasRevenueSplits) {
                actions.push({
                  onClick: () => handleManageRevenueSplitClick(row),
                  dataTestId: isViewOnly
                    ? "project-view-revenue-split"
                    : "project-manage-revenue-split",
                  label: getHistoryLabel(
                    isViewOnly
                      ? "viewRevenueSplitAction"
                      : "manageRevenueSplitAction",
                  ),
                });
              }

              actions.push({
                onClick: () => handleOnExportInvoiceClick(row),
                dataTestId: "export-invoice",
                label: commonLabels.exportToExcel,
              });

              actions.push({
                onClick: () => handleOnDownloadInvoiceClick(row),
                dataTestId: "download-invoice",
                label: commonLabels.downloadInvoice,
              });
              return <MuiOptionMenu menuItems={actions} />;
            },
          },
        ]
      : []),
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm: React.JSX.Element[] = [
    <FormController
      control={control}
      name={ProjectInvoiceHistoryColumnKey.projectNumber}
      key={ProjectInvoiceHistoryColumnKey.projectNumber}
    >
      <MuiTextField
        id={`${pageId}-${ProjectInvoiceHistoryColumnKey.projectNumber}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      clearable
      name={ProjectInvoiceHistoryColumnKey.projectDate}
      key={ProjectInvoiceHistoryColumnKey.projectDate}
      format={userCountryDateFormat}
      valueFormat={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,

    <FormController
      control={control}
      name={ProjectInvoiceHistoryColumnKey.invoiceStatusId}
      key={ProjectInvoiceHistoryColumnKey.invoiceStatusId}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          projectInvoiceHistorySiteCoreKeys.invoiceHistoryTitle,
          projectInvoiceHistorySiteCoreKeys.invoiceStatusList,
        )}
        visibleDefaultSelectOption={true}
        value=""
        id={`${pageId}-${ProjectInvoiceHistoryColumnKey.invoiceStatusId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectInvoiceHistoryColumnKey.projectReversalReason}
      key={ProjectInvoiceHistoryColumnKey.projectReversalReason}
    >
      <MuiTextField
        id={`${pageId}-${ProjectInvoiceHistoryColumnKey.projectReversalReason}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectInvoiceHistoryColumnKey.projectTotalInvoiceAmount}
      key={ProjectInvoiceHistoryColumnKey.projectTotalInvoiceAmount}
    >
      <MuiTextField
        id={`${pageId}-${ProjectInvoiceHistoryColumnKey.projectTotalInvoiceAmount}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectInvoiceHistoryColumnKey.projectIssuedBy}
      key={ProjectInvoiceHistoryColumnKey.projectIssuedBy}
    >
      <MuiTextField
        id={`${pageId}-${ProjectInvoiceHistoryColumnKey.projectIssuedBy}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ProjectInvoiceHistoryColumnKey.projectPurchaseOrder}
      key={ProjectInvoiceHistoryColumnKey.projectPurchaseOrder}
    >
      <MuiTextField
        id={`${pageId}-${ProjectInvoiceHistoryColumnKey.projectPurchaseOrder}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <></>,
  ];

  return (
    <MuiTable
      ref={invoiceHistoryTableRef}
      pageId={pageId}
      isLastColumnSticky={hasModulePermission(
        MODULE_PERMISSIONS.PROGRAM_INVOICE_REVERSAL,
      )}
      tableHeaderForm={tableHeaderForm}
      columns={columns}
      tableResults={
        state.filteredListInvoiceHistory as unknown as Record<
          string,
          RowValue
        >[]
      }
      noDataInApi={state.originalListInvoiceHistory.length === 0}
      noResultMessage={commonLabels.searchCriteria}
      priorityResults={[]}
    />
  );
};
